import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  MessageCircle,
  User,
} from "lucide-react";

import { site } from "../data/site";

/**
 * Same-origin, and deliberately so.
 *
 * This used to POST to https://api.astalabista.com — the Agent Labista
 * backend. That API was decommissioned and its DNS record removed, so every
 * enquiry from this form failed silently for however long it had been gone.
 * The endpoint now ships with the site (functions/api/contact.js), which
 * means it cannot be torn down separately, and there is no CORS list to keep
 * correct on someone else's service.
 */
const CONTACT_ENDPOINT = "/api/contact";

type FormState = "idle" | "loading" | "ok" | "error";

/**
 * What the enquiry is about. Now a first-class field rather than a prefix
 * smuggled into the message body — the old endpoint was a shared marketing
 * route whose triage filtered on an exact `source` string, so anything extra
 * risked being dropped. Ours reads it directly and puts it in the subject
 * line.
 */
const INTERESTS = [
  "Consulting",
  "Software development",
  "Agent Labista",
  "Hazri Book",
  "Something else",
] as const;

export function ContactForm({ compact }: { compact?: boolean }) {
  const [state, setState] = useState<FormState>("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [interest, setInterest] = useState<string>(INTERESTS[0]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setState("loading");
    setErrMsg(null);

    const data = new FormData(form);

    const body = {
      name: String(data.get("name") || "").trim(),
      company: String(data.get("company") || "").trim(),
      email: String(data.get("email") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      message: String(data.get("message") || "").trim(),
      interest,
      // Honeypot — hidden from people, filled in by bots.
      website: String(data.get("website") || ""),
    };

    if (!body.name || !body.email) {
      setState("error");
      setErrMsg("Name and email are required.");
      return;
    }

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (!res.ok || !result.ok) {
        // Show the endpoint's own message when it gave one — it is written
        // for the sender ("that email doesn't look right"), not for a log.
        throw new Error(result.error || `Something went wrong (HTTP ${res.status}).`);
      }
      setState("ok");
      form.reset();
    } catch (err) {
      setState("error");
      setErrMsg(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please email contact@astalabista.com instead.",
      );
    }
  }

  if (state === "ok") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-8 text-center"
      >
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-emerald-500/90 text-ink-950">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="mt-4 font-display text-xl font-semibold">Got it.</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-white/70">
          We read every enquiry ourselves and reply within one working day.
          If it is urgent, mail{" "}
          <a
            href={`mailto:${site.email}`}
            className="text-emerald-300 underline-offset-2 hover:underline"
          >
            {site.email}
          </a>{" "}
          directly.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`grid gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] ${
        compact ? "p-4" : "p-5 sm:p-6"
      }`}
    >
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">
          What is this about?
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {INTERESTS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setInterest(opt)}
              aria-pressed={interest === opt}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                interest === opt
                  ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-200"
                  : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/25 hover:text-white"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-1 grid gap-3 sm:grid-cols-2">
        <Field icon={User} name="name" label="Your name" required autoComplete="name" />
        <Field
          icon={Building2}
          name="company"
          label="Company"
          autoComplete="organization"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          icon={Mail}
          name="email"
          type="email"
          label="Work email"
          required
          autoComplete="email"
        />
        <Field
          icon={MessageCircle}
          name="phone"
          type="tel"
          label="Phone (optional)"
          autoComplete="tel"
        />
      </div>
      <Field
        icon={MessageCircle}
        name="message"
        label="What are you trying to build, automate or improve?"
        as="textarea"
      />

      {/* Honeypot. Hidden from people and from assistive tech; bots fill
       *  every field they find, so anything here means "not a human". */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <button
        type="submit"
        disabled={state === "loading"}
        className="group mt-1 flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-base font-semibold text-ink-950 shadow-glow transition-transform hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70"
      >
        {state === "loading" ? (
          <>
            <Loader2 className="size-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Start a conversation
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      {state === "error" ? (
        <div role="alert" className="text-xs text-rose-300">
          {errMsg ?? "Couldn't submit."}
        </div>
      ) : (
        <p className="text-center text-[11px] text-white/40">
          Or email{" "}
          <a
            href={`mailto:${site.email}`}
            className="text-white/70 transition-colors hover:text-white"
          >
            {site.email}
          </a>
          . We reply within one working day.
        </p>
      )}
    </form>
  );
}

function Field({
  icon: Icon,
  name,
  label,
  type = "text",
  required,
  as,
  autoComplete,
}: {
  icon: typeof User;
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  as?: "textarea";
  autoComplete?: string;
}) {
  const inputId = `f-${name}`;
  const base =
    "w-full bg-transparent text-sm text-white placeholder:text-white/35 focus:outline-none";

  return (
    <div className="flex flex-col gap-1.5">
      {/* Visible labels rather than placeholder-only fields: a placeholder
       *  disappears the moment someone starts typing, which is exactly when
       *  they need to check what the field was. */}
      <label
        htmlFor={inputId}
        className="text-[11px] font-medium uppercase tracking-wider text-white/45"
      >
        {label}
        {required ? <span className="ml-1 text-emerald-400">*</span> : null}
      </label>
      <div className="flex gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3 transition-colors focus-within:border-white/30">
        <Icon className="mt-0.5 size-4 shrink-0 text-white/40" aria-hidden />
        {as === "textarea" ? (
          <textarea
            id={inputId}
            name={name}
            rows={4}
            className={`${base} resize-none`}
          />
        ) : (
          <input
            id={inputId}
            type={type}
            name={name}
            required={required}
            autoComplete={autoComplete}
            className={base}
          />
        )}
      </div>
    </div>
  );
}
